import { useState } from "react";
import { MoreVertical } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  useSendScheduledEntityNow,
  useRescheduleEntity,
  useDeleteShareableEntity,
  type ShareableEntity,
} from "@/lib/api/shareable-entities";
import { DeleteScheduledModal } from "./scheduling/delete-scheduled-modal";
import { ScheduleModal } from "./scheduling/schedule-modal";
import { hubToast } from "./scheduling/hub-toasts";
import { useAbility } from "@/lib/casl/ability-context";

interface ScheduledEntityActionsProps {
  entity: ShareableEntity;
}

export function ScheduledEntityActions({
  entity,
}: ScheduledEntityActionsProps) {
  const ability = useAbility();
  const canUpdate = ability.can("update", "shareable-entities");
  const canDelete = ability.can("delete", "shareable-entities");

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);

  const sendNow = useSendScheduledEntityNow();
  const reschedule = useRescheduleEntity();
  const deleteEntity = useDeleteShareableEntity();

  if (!canUpdate && !canDelete) return null;

  const entityType = entity.entityType;

  const handleSendNow = async () => {
    try {
      await sendNow.mutateAsync({ id: entity.id });
      hubToast.sent(entityType);
    } catch (error) {
      console.error("Failed to send now:", error);
    }
  };

  const handleChangeSchedule = async (newScheduledAt: Date) => {
    try {
      await reschedule.mutateAsync({
        id: entity.id,
        scheduleAt: newScheduledAt.toISOString(),
      });
      hubToast.scheduleChanged(entityType);
      setIsScheduleModalOpen(false);
    } catch (error) {
      console.error("Failed to update schedule:", error);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteEntity.mutateAsync({ id: entity.id });
      hubToast.deleted(entityType);
      setIsDeleteModalOpen(false);
    } catch (error) {
      console.error("Failed to delete:", error);
    }
  };

  const isPending =
    sendNow.isPending || reschedule.isPending || deleteEntity.isPending;

  return (
    <>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <button
            className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-gray-100"
            aria-label="More actions"
            disabled={isPending}
          >
            <MoreVertical className="h-5 w-5 text-gray-600" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {canUpdate && (
            <DropdownMenuItem onClick={handleSendNow} disabled={isPending}>
              Send Now
            </DropdownMenuItem>
          )}
          {canUpdate && (
            <DropdownMenuItem
              onClick={() => setIsScheduleModalOpen(true)}
              disabled={isPending}
            >
              Change Schedule
            </DropdownMenuItem>
          )}
          {canDelete && (
            <DropdownMenuItem
              onClick={() => setIsDeleteModalOpen(true)}
              disabled={isPending}
              className="text-destructive"
            >
              Delete
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <DeleteScheduledModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
        loading={deleteEntity.isPending}
      />

      <ScheduleModal
        isOpen={isScheduleModalOpen}
        onClose={() => setIsScheduleModalOpen(false)}
        onSchedule={handleChangeSchedule}
        entityType={entityType}
        initialDate={
          entity.scheduledAt ? new Date(entity.scheduledAt) : undefined
        }
        loading={reschedule.isPending}
      />
    </>
  );
}
